import { Component, OnInit } from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router'
import { Checkout, Depot } from 'depoto-core/src/entities'
import { CoreService, NotificationService } from '../../services'
import { HistoryUtil, SchemaUtil } from '../../utils'
import { AbstractControl, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms'
import { CheckDataChanges } from '../../services/can-deactivate-guard.service'
import { Institution } from 'depoto-core/src/entities/Institution'
import * as Sentry from '@sentry/angular-ivy'
import { TranslateService } from '@ngx-translate/core'

@Component({
  selector: 'app-checkout',
  templateUrl: 'checkout.component.html',
})
export class CheckoutComponent implements OnInit, CheckDataChanges {
  checkout: Checkout
  public institutions: Institution[] = []
  public institutionsWithNullOption: Institution[] = []
  depots: Depot[]
  isCreating = false
  loading = true
  submitted = false

  checkoutForm = new UntypedFormGroup({
    name: new UntypedFormControl({ value: '', disabled: false }, [Validators.required]),
    depots: new UntypedFormControl({ value: [], disabled: false }, []),
  })

  constructor(
    private route: ActivatedRoute,
    public router: Router,
    private notificationService: NotificationService,
    private translateService: TranslateService,
    private core: CoreService,
  ) {}

  ngOnInit() {
    const id = this.route.snapshot.params['id']
    if (isNaN(id)) {
      this.isCreating = true
      this.checkout = new Checkout()
      this.refreshData(null)
    } else {
      this.refreshData(id)
    }
  }

  fillFormValues() {
    this.checkoutForm.setValue({
      name: this.checkout.name || '',
      depots: this.checkout.depots || [],
    })
  }

  getFormValue() {
    this.checkout.name = this.checkoutForm.value.name
    this.checkout.depots = this.checkoutForm.value.depots
  }

  hasUnsavedDataChanges(): boolean {
    return false
  }

  async refreshData(id?: number) {
    this.loading = true
    const company = this.core.services.storage.getSync(this.core.services.storage.keys.auth.company)
    const companyFilterId = company ? company.id : null
    this.institutions = this.core.lists.institutions
    this.institutionsWithNullOption = [null, ...this.core.lists.institutions]
    this.depots = this.core.lists.depots
    if (id && id > 0) {
      this.checkout = await this.core.services.checkout.getById(id, SchemaUtil.checkout)
      if (this.checkout?.id > 0) {
        this.fillFormValues()
      } else {
        this.notificationService.error(this.translateService.instant('alert.checkout.error'))
      }
    }
    this.loading = false
  }

  selectInstitution(event) {
    if (event) {
      this.checkout.institution = this.institutions.find(institution => institution.id === Number(event))
    } else {
      this.checkout.institution = new Institution()
      this.checkout.institution.id = null
    }
  }

  async createCheckout() {
    this.submitted = true
    if (this.checkoutForm.invalid) {
      this.loading = false
      return
    }
    this.loading = true
    try {
      this.getFormValue()
      const result = await this.core.services.checkout.create(this.checkout)
      this.notificationService.success(this.translateService.instant('alert.checkout.created'))
      this.loading = false
      if (result) {
        this.router.navigate(['/surgery/', result.id])
      }
    } catch (e) {
      console.log(e)
    }
  }

  async updateCheckout() {
    this.submitted = true
    if (this.checkoutForm.invalid) {
      return
    }
    try {
      this.loading = true
      this.getFormValue()
      const result = await this.core.services.checkout.updatePart(this.checkout)
      this.notificationService.success(this.translateService.instant('alert.checkout.updated'))
      if (result.id > 0) {
        return this.refreshData(result.id)
      }
    } catch (err) {
      console.log(err)
    } finally {
      this.loading = false
    }
  }

  async deleteCheckout() {
    const confirmation = confirm(
      this.translateService.instant('checkout.delete.confirmation', { name: this.checkout.name }),
    )
    if (confirmation) {
      try {
        await this.core.services.checkout.delete(this.checkout)
        this.notificationService.success(this.translateService.instant('alert.checkout.deleted'))
        HistoryUtil.navigateBack()
      } catch (err) {
        console.log(err)
      }
    }
  }

  toggleDepot(depot: Depot, event: any) {
    if (event.target.checked) {
      this.checkoutForm.value.depots.push(depot)
    } else {
      this.checkoutForm.value.depots.forEach((d: Depot, i: number) => {
        if (depot.id === d.id && depot.name === d.name) {
          this.checkoutForm.value.depots.splice(i, 1)
        }
      })
    }
  }

  isChosenDepot(depot: Depot): boolean {
    let exists = false
    this.checkoutForm.value.depots.forEach((d: Depot) => {
      if (d.id === depot.id && d.name === depot.name) {
        exists = true
        return
      }
    })
    return exists
  }

  get formControls(): { [key: string]: AbstractControl } {
    return this.checkoutForm.controls
  }

  triggerBackBtn() {
    if (window.history.length > 1) {
      window.history.back()
    } else {
      this.router.navigate(['/surgeries'])
    }
  }
}
